import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is a CSSBuy spreadsheet and how does it work?',
    answer: 'A CSSBuy spreadsheet is a curated directory of product listings from various sellers on the CSSBuy platform. It organizes thousands of fashion items — including sneakers, clothing, accessories, and luxury goods — into easy-to-browse categories with direct purchase links, pricing information, seller ratings, and QC photo references. Our spreadsheet is updated daily to ensure accuracy and freshness.',
  },
  {
    question: 'How do I use a CSSBuy spreadsheet to find the best deals?',
    answer: 'Start by browsing the category that interests you most — whether it is sneakers, hoodies, or accessories. Each listing includes key details such as the seller name, price range, and available sizes. Look for items marked as verified with high seller ratings. You can use the built-in comparison features to evaluate similar products across different sellers. Our guide also includes tips on how to negotiate prices and combine shipping for maximum savings.',
  },
  {
    question: 'Are the sellers in this CSSBuy spreadsheet verified and trustworthy?',
    answer: 'Yes. Every seller listed in our spreadsheet has been vetted by our community of experienced CSSBuy shoppers. We check for consistent product quality, accurate item descriptions, timely shipping, responsive customer service, and positive buyer feedback history. Sellers must maintain a high satisfaction rate to remain in our directory. We also regularly remove sellers whose quality declines.',
  },
  {
    question: 'How often is the CSSBuy spreadsheet updated?',
    answer: 'Our spreadsheet receives daily updates. New product listings are added as soon as they are verified by our team. Prices, inventory status, and seller ratings are refreshed every 24 hours. If a seller discontinues an item or significantly changes pricing, our directory reflects those changes promptly — unlike static spreadsheets that can be outdated for weeks or months.',
  },
  {
    question: 'What categories are available in the CSSBuy spreadsheet?',
    answer: 'Our directory covers 14 comprehensive categories: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category is further organized by brand, style, and price range for easy navigation.',
  },
  {
    question: 'Do you offer QC (Quality Control) guides for CSSBuy purchases?',
    answer: 'Absolutely. Our comprehensive QC guide teaches you exactly what to look for when reviewing product photos before shipping. We cover stitching inspection, logo placement verification, material quality assessment, color accuracy checking, and sizing confirmation. We also provide side-by-side comparison examples to help you spot potential flaws quickly and confidently.',
  },
  {
    question: 'Can beginners use this CSSBuy spreadsheet effectively?',
    answer: 'Yes! Our spreadsheet is designed with beginners in mind. We provide a step-by-step getting started guide that walks you through creating a CSSBuy account, placing your first order, understanding shipping options, and using QC photos. The interface is intuitive, and every category includes helpful tips specifically for first-time CSSBuy shoppers.',
  },
  {
    question: 'What makes this CSSBuy spreadsheet different from others?',
    answer: 'Unlike random forum spreadsheets or unverified lists, our directory is professionally maintained with daily updates, verified seller checks, comprehensive QC tutorials, and detailed shipping guides. We also provide category comparisons, trend analysis, and community-driven ratings. Our commitment to accuracy and freshness is unmatched — most other spreadsheets are updated infrequently and lack proper seller verification.',
  },
  {
    question: 'How do I get updates when new items are added to the spreadsheet?',
    answer: 'Our spreadsheet is continuously refreshed, and you can bookmark this page or visit regularly to see the latest additions. The Trending Now section highlights the hottest new finds each week. We recommend checking back frequently, especially before major shopping seasons, as inventory and seller availability can change rapidly.',
  },
  {
    question: 'Is the CSSBuy spreadsheet available for free?',
    answer: 'Yes, our entire CSSBuy spreadsheet directory is completely free to access. There are no paywalls, subscription fees, or hidden costs. We believe in making verified fashion finds accessible to everyone in the community. Simply browse, click on any category that interests you, and start discovering great products from trusted sellers.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full border border-background-300/80 text-xs font-semibold text-foreground-500 font-label tracking-wide mb-4">
            FAQ
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            Everything you need to know about using CSSBuy spreadsheets
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-background-200/70 overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
              >
                <h3 className="text-sm font-semibold text-foreground-800 pr-4">
                  {faq.question}
                </h3>
                <span
                  className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-200 ${
                    openIndex === index
                      ? 'bg-accent-500 text-white rotate-45'
                      : 'bg-background-100 text-foreground-400'
                  }`}
                >
                  <i className="ri-add-line text-sm"></i>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-5' : 'max-h-0'
                }`}
              >
                <p className="px-6 text-sm text-foreground-500 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/article/cssbuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors"
          >
            Read our detailed guide for more answers
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}